/* global Word */
import {
  delimiterFragments,
  isFullTextVerified,
  remainingSearchChunks,
  shouldAcceptDelimiterMatch,
  truncateSearchText,
  type LocateResult,
  type WriteAction,
} from "./locate-text.policy";

export type { LocateMissReason, LocateResult, WriteAction } from "./locate-text.policy";
export {
  getWriteActionForQuery,
  locateMissMessage,
  truncateSearchText,
} from "./locate-text.policy";

export interface LocateSearchOptions {
  matchCase?: boolean;
  matchWholeWord?: boolean;
  ignorePunct?: boolean;
  ignoreSpace?: boolean;
}

export const CASE_INSENSITIVE_SEARCH: LocateSearchOptions = {
  matchCase: false,
  matchWholeWord: false,
};

export const DOCUMENT_SEARCH: LocateSearchOptions = {
  ignorePunct: true,
  ignoreSpace: true,
  matchCase: false,
  matchWholeWord: false,
};

const ADJACENT_BEFORE = "AdjacentBefore";

export async function locateFullText(
  context: Word.RequestContext,
  originalText: string,
  searchOptions: LocateSearchOptions = CASE_INSENSITIVE_SEARCH
): Promise<LocateResult<Word.Range>> {
  if (originalText === "") return { ok: false, reason: "not-found" };

  const truncated = truncateSearchText(originalText);
  const results = context.document.body.search(truncated, searchOptions);
  results.load("items");
  await context.sync();

  if (results.items.length === 0) return { ok: false, reason: "not-found" };

  const verified: Word.Range[] = [];
  for (const item of results.items) {
    if (item == null) continue;
    const full = await expandAndVerify(context, item, originalText, searchOptions);
    if (full != null) verified.push(full);
  }

  if (verified.length === 0) return { ok: false, reason: "unverified" };
  return { ok: true, ranges: verified };
}

export async function locateUniqueDelimiterFragment(
  context: Word.RequestContext,
  truncatedText: string,
  searchOptions: LocateSearchOptions = DOCUMENT_SEARCH
): Promise<LocateResult<Word.Range>> {
  const fragments = delimiterFragments(truncatedText);
  let sawNonUnique = false;

  for (const fragment of fragments) {
    const result = await searchUniqueFragment(context, fragment, searchOptions);
    if (result.ok) return result;
    if (result.reason === "not-unique") sawNonUnique = true;
  }

  return { ok: false, reason: sawNonUnique ? "not-unique" : "not-found" };
}

export type SelectLocateResult =
  | { found: true; count: number; via: "full" | "delimiter" }
  | { found: false; reason: "not-found" | "not-unique" | "unverified" };

export async function locateAndSelectInDocument(
  originalText: string,
  searchOptions: LocateSearchOptions = DOCUMENT_SEARCH
): Promise<SelectLocateResult> {
  return Word.run(async (context) => {
    const located = await locateFullText(context, originalText, searchOptions);
    if (located.ok && (await selectFirstRange(context, located.ranges))) {
      return { found: true, count: located.ranges.length, via: "full" };
    }
    const delim = await locateUniqueDelimiterFragment(
      context,
      truncateSearchText(originalText),
      searchOptions
    );
    if (delim.ok && (await selectFirstRange(context, delim.ranges))) {
      return { found: true, count: 1, via: "delimiter" };
    }
    const reason = !delim.ok ? delim.reason : "not-found";
    return { found: false, reason };
  });
}

export function writeLocatedText(range: Word.Range, text: string, action: WriteAction): void {
  const location =
    action === "insert-after" ? Word.InsertLocation.after : Word.InsertLocation.replace;
  range.insertText(text, location);
}

async function expandAndVerify(
  context: Word.RequestContext,
  prefixRange: Word.Range,
  originalText: string,
  searchOptions: LocateSearchOptions
): Promise<Word.Range | null> {
  prefixRange.load("text");
  await context.sync();
  if (isFullTextVerified(prefixRange.text, originalText)) return prefixRange;

  const chunks = remainingSearchChunks(originalText);
  if (chunks.length === 0) return null;

  let expanded = prefixRange;
  for (const chunk of chunks) {
    const next = await findAdjacentAfter(context, expanded, chunk, searchOptions);
    if (next == null) return null;
    expanded = expanded.expandTo(next);
  }

  expanded.load("text");
  await context.sync();
  return isFullTextVerified(expanded.text, originalText) ? expanded : null;
}

async function findAdjacentAfter(
  context: Word.RequestContext,
  current: Word.Range,
  chunk: string,
  searchOptions: LocateSearchOptions
): Promise<Word.Range | null> {
  const results = context.document.body.search(chunk, searchOptions);
  results.load("items");
  await context.sync();

  for (const item of results.items) {
    if (item == null) continue;
    const relation = current.compareLocationWith(item);
    await context.sync();
    if (String(relation.value) === ADJACENT_BEFORE) return item;
  }
  return null;
}

async function searchUniqueFragment(
  context: Word.RequestContext,
  fragment: string,
  searchOptions: LocateSearchOptions
): Promise<LocateResult<Word.Range>> {
  const results = context.document.body.search(fragment, searchOptions);
  results.load("items");
  await context.sync();
  const items = results.items.filter((item): item is Word.Range => item != null);
  if (!shouldAcceptDelimiterMatch(fragment, items.length)) {
    return { ok: false, reason: items.length > 1 ? "not-unique" : "not-found" };
  }
  const only = items[0];
  if (only == null) return { ok: false, reason: "not-found" };
  return { ok: true, ranges: [only] };
}

async function selectFirstRange(
  context: Word.RequestContext,
  ranges: Word.Range[]
): Promise<boolean> {
  const first = ranges[0];
  if (first == null) return false;
  first.select();
  await context.sync();
  return true;
}
